import { useEffect, useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { CheckIcon, ChevronDownIcon, KeyRoundIcon } from 'lucide-react';
import { dexToast } from '@dexmate/dexui';
import { getAuthToken } from '@/lib/auth';

/** Test tokens recognised by workers/auth-stub-dev. Keep in sync.
 *  Canonical list: docs/manual-tests/auth-tokens.md and
 *  backend/tests/wiremock/fixtures/README.md. */
interface TestRole {
  token: string;
  label: string;
  scopes: string;
  tenant: 'A' | 'B';
}

const TEST_ROLES: ReadonlyArray<TestRole> = [
  // --- Tenant A: default dev tenant ---
  {
    token: 's2s_test_admin',
    label: 'Admin',
    scopes: 'data:read · data:admin',
    tenant: 'A',
  },
  {
    token: 's2s_test_operator',
    label: 'Operator',
    scopes: 'data:upload · data:read',
    tenant: 'A',
  },
  {
    token: 's2s_test_annotator',
    label: 'Annotator',
    scopes: 'data:read · data:annotate',
    tenant: 'A',
  },
  // Second actors per role — multi-actor workflow tests
  {
    token: 's2s_test_admin_2',
    label: 'Admin (2nd)',
    scopes: 'data:read · data:admin',
    tenant: 'A',
  },
  {
    token: 's2s_test_operator_2',
    label: 'Operator (2nd)',
    scopes: 'data:upload · data:read',
    tenant: 'A',
  },
  {
    token: 's2s_test_annotator_2',
    label: 'Annotator (2nd)',
    scopes: 'data:read · data:annotate',
    tenant: 'A',
  },
  // --- Tenant B: cross-tenant isolation smoke ---
  {
    token: 's2s_test_admin_tenant_b',
    label: 'Admin · Tenant B',
    scopes: 'data:read · data:admin',
    tenant: 'B',
  },
];

const TOKEN_KEY = 'DEXUPLOAD_TOKEN';

/**
 * RoleDropdown — dev-only role-switch menu. Click the role pill in the
 * header to open; selecting a role writes localStorage and reloads the
 * page so all queries re-fetch with the new identity.
 *
 * This is replaced by real dexhub SSO when the introspection endpoint ships;
 * the local-storage override is a development convenience only.
 */
export function RoleDropdown() {
  const [token, setToken] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  // Read the current token on mount (and after any session-storage event)
  useEffect(() => {
    setToken(getAuthToken());
    const onStorage = () => setToken(getAuthToken());
    window.addEventListener('storage', onStorage);
    // AuthBanner emits this event when its 'Switch role' button is clicked.
    const onOpenRequest = () => setOpen(true);
    window.addEventListener('cds:open-role-dropdown', onOpenRequest);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('cds:open-role-dropdown', onOpenRequest);
    };
  }, []);

  const current = TEST_ROLES.find((r) => r.token === token);
  const display = current
    ? current.label
    : token
      ? token.split('_').slice(0, 3).join('_')
      : 'Sign in';

  const onSelect = (role: TestRole) => {
    if (role.token === token) return;
    localStorage.setItem(TOKEN_KEY, role.token);
    dexToast.success(`Switched to ${role.label}`, {
      description: `Reloading with ${role.scopes}.`,
    });
    // Brief delay so the toast renders before the page goes away.
    setTimeout(() => location.reload(), 600);
  };

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label="Switch role"
          className="inline-flex min-h-9 items-center gap-1.5 rounded-full border border-dex-border bg-dex-bg-elevated px-3 py-1 text-xs font-medium text-dex-text-secondary transition-colors hover:bg-dex-bg-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dex-accent"
        >
          <span
            aria-hidden
            className={`size-1.5 rounded-full ${token ? 'bg-dex-status-success' : 'bg-dex-status-neutral'}`}
          />
          {display}
          <ChevronDownIcon aria-hidden className="size-3.5 text-dex-text-tertiary" />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-30 min-w-[260px] overflow-hidden rounded-lg border border-dex-border bg-dex-bg-elevated p-1 shadow-lg"
        >
          <DropdownMenu.Label className="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-dex-text-hint">
            Tenant A · default dev tenant
          </DropdownMenu.Label>
          {TEST_ROLES.filter((r) => r.tenant === 'A').map((role) => {
            const active = role.token === token;
            return (
              <DropdownMenu.Item
                key={role.token}
                onSelect={() => onSelect(role)}
                className="flex cursor-pointer items-start gap-2 rounded-md px-2 py-2 text-sm outline-none focus:bg-dex-nav-hover-bg data-[highlighted]:bg-dex-nav-hover-bg"
              >
                <span aria-hidden className="mt-0.5 size-4 shrink-0 text-dex-status-info">
                  <KeyRoundIcon className="size-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium text-dex-text-primary">
                      {role.label}
                    </span>
                    {active && (
                      <CheckIcon
                        aria-label="Current"
                        className="size-3.5 text-dex-status-success"
                      />
                    )}
                  </div>
                  <div className="truncate text-[11px] text-dex-text-tertiary">
                    {role.scopes}
                  </div>
                </div>
              </DropdownMenu.Item>
            );
          })}
          <DropdownMenu.Separator className="my-1 h-px bg-dex-border" />
          <DropdownMenu.Label className="px-2 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wider text-dex-text-hint">
            Tenant B · isolation smoke
          </DropdownMenu.Label>
          {TEST_ROLES.filter((r) => r.tenant === 'B').map((role) => {
            const active = role.token === token;
            return (
              <DropdownMenu.Item
                key={role.token}
                onSelect={() => onSelect(role)}
                className="flex cursor-pointer items-start gap-2 rounded-md px-2 py-2 text-sm outline-none focus:bg-dex-nav-hover-bg data-[highlighted]:bg-dex-nav-hover-bg"
              >
                <span aria-hidden className="mt-0.5 size-4 shrink-0 text-dex-status-warning">
                  <KeyRoundIcon className="size-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium text-dex-text-primary">
                      {role.label}
                    </span>
                    {active && (
                      <CheckIcon
                        aria-label="Current"
                        className="size-3.5 text-dex-status-success"
                      />
                    )}
                  </div>
                  <div className="truncate text-[11px] text-dex-text-tertiary">
                    {role.scopes}
                  </div>
                </div>
              </DropdownMenu.Item>
            );
          })}
          <DropdownMenu.Separator className="my-1 h-px bg-dex-border" />
          <div className="px-2 py-1.5 text-[10px] leading-relaxed text-dex-text-hint">
            Token is stored in localStorage and replaced when dexhub SSO ships.
            See <code className="font-mono">docs/manual-tests/auth-tokens.md</code>.
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
